/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import { useMemo } from 'react';

import { cssMap, jsx } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl';

import StatusWarningIcon from '@atlaskit/icon/core/status-warning';
import { MediaClientProvider } from '@atlaskit/media-client-react/media-client-provider';
import type { AuthProvider } from '@atlaskit/media-core/auth';
import { MediaImage } from '@atlaskit/media-image';
import { Box } from '@atlaskit/primitives/compiled';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import { token } from '@atlaskit/tokens';

import { useProductDependencies } from '../../../controllers/use-product-dependencies';
import { messages } from './messages';

const IMAGE_CONFIGURATION = {
	allowAnimated: true,
	mode: 'fit',
	upscale: false,
} as const;

const styles = cssMap({
	loading: {
		width: '100%',
		height: '100%',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		backgroundColor: token('color.background.accent.gray.subtlest'),
	},
	errorWrapper: {
		display: 'flex',
		flexDirection: 'column',
		width: '100%',
		height: '100%',
		justifyContent: 'center',
		alignItems: 'center',
		backgroundColor: token('color.background.accent.gray.subtlest'),
	},
	image: {
		height: '100%',
		width: '100%',
		aspectRatio: 'unset',
		objectFit: 'cover',
	},
});

export type CustomImageUploadProps = {
	contentId: string;
	imageAltText?: string;
	imageSrc?: string;
	/** Explicit media config override; if absent we fall back to provider. */
	mediaConfigOverride?: {
		authProvider?: AuthProvider;
		collection?: string;
	};
	shouldShowErrorMessage?: boolean;
};

export const CustomImageUploadRenderer = ({
	contentId,
	imageSrc = '',
	imageAltText,
	mediaConfigOverride,
	shouldShowErrorMessage = false,
}: CustomImageUploadProps): JSX.Element | null => {
	const intl = useIntl();
	const { experienceSuccessComponent, experienceStopComponent, useMediaConfig } =
		useProductDependencies();

	const { authProvider, collection, fileId } = useMediaConfig?.({ contentId, imageSrc }) || {};

	const resolvedAuthProvider = mediaConfigOverride?.authProvider ?? authProvider;
	const resolvedCollectionName = mediaConfigOverride?.collection ?? collection;

	const identifier = useMemo(
		() => ({
			mediaItemType: 'file' as const,
			id: fileId ?? '',
			collectionName: resolvedCollectionName ?? '',
		}),
		[fileId, resolvedCollectionName],
	);

	if (!resolvedAuthProvider || !resolvedCollectionName || !imageSrc) {
		return null;
	}

	const errorState = (
		<Box xcss={styles.errorWrapper} testId="custom-image-upload-error">
			<StatusWarningIcon
				label={intl.formatMessage(messages.warningIcon)}
				color={token('color.icon.subtle')}
			/>
			{shouldShowErrorMessage && <FormattedMessage {...messages.imageFailedMessage} />}
		</Box>
	);

	const ExperienceSuccess = experienceSuccessComponent || (() => null);
	const ExperienceError = experienceStopComponent || (() => null);

	return (
		<MediaClientProvider clientConfig={{ authProvider: resolvedAuthProvider }}>
			<MediaImage
				identifier={identifier}
				// Force rerender image on ID change until https://product-fabric.atlassian.net/browse/CXP-2899 is fixed
				key={fileId}
				mediaClientConfig={{ authProvider: resolvedAuthProvider }}
				apiConfig={IMAGE_CONFIGURATION}
			>
				{({ data, loading, error }) => {
					if (loading) {
						return (
							<UFOLoadHold name="custom-uploaded-image-loading">
								<Box testId="custom-image-upload-loading" xcss={styles.loading} />
							</UFOLoadHold>
						);
					}

					if (error || !data?.src) {
						return (
							<>
								{errorState}
								<ExperienceError
									error={new Error('Image failed to load in CustomImageUploadRenderer')}
								/>
							</>
						);
					}

					return (
						<>
							<img
								css={styles.image}
								src={data.src}
								alt={imageAltText}
								data-testid="custom-image-upload"
							/>
							<ExperienceSuccess />
						</>
					);
				}}
			</MediaImage>
		</MediaClientProvider>
	);
};
